import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselPrevious,
  CarouselNext,
} from './ui/carousel';
import { Home, Maximize2, Check } from 'lucide-react';
import { BROCHURE_DATA, APARTMENT_TYPES_V2, RESORT_SHOWCASE } from '../lib/brochure';
import { fmtEUR } from '../lib/financial';

// Stable Carousel options (avoid recreating object on each render).
const CAROUSEL_OPTS = { align: 'start', loop: true };

// Immersive "walk through the resort" experience: apartment types with room-by-room carousel
// + amenities showcase (masterplan, architecture, restaurant, spa, development).
// Per-resort assets via BROCHURE_DATA[slug].
// Inspired by grandbrilliant.al but simpler — for Lombok & Spille.

export default function ApartmentShowcase({ slug }) {
  const { t } = useTranslation();
  const data = BROCHURE_DATA[slug];
  const apartmentTypes = data?.apartmentTypes || APARTMENT_TYPES_V2;
  const showcase = data?.showcase || RESORT_SHOWCASE;

  const [typeIdx, setTypeIdx] = useState(0);
  const type = apartmentTypes[typeIdx];

  return (
    <div className="space-y-10 md:space-y-14" data-testid={`apartment-showcase-${slug}`}>
      {/* Apartment-level showcase */}
      <section>
        <div className="flex items-end justify-between flex-wrap gap-3 mb-5 md:mb-6">
          <div>
            <div className="text-[10px] md:text-xs tracking-[0.3em] uppercase text-muted-foreground">{t('resortDetail.showcase.eyebrow', 'Le residenze')}</div>
            <h2 className="font-serif text-2xl md:text-3xl lg:text-5xl text-[hsl(var(--primary))] mt-1 leading-tight">{t('resortDetail.showcase.title', 'Entra in ogni suite.')}</h2>
            <p className="text-xs md:text-sm text-muted-foreground mt-1.5 md:mt-2 max-w-2xl">
              {t('resortDetail.showcase.subtitle', 'Quattro tipologie, tutte chiavi in mano. Scegli la tua e scorri stanza per stanza.')}
            </p>
          </div>
        </div>

        {/* Tabs by apartment type — scroll horizontale on mobile */}
        <div className="-mx-4 md:mx-0 px-4 md:px-0 overflow-x-auto scrollbar-none mb-5">
          <div className="flex flex-nowrap md:flex-wrap gap-2 min-w-min" role="tablist" data-testid="apt-type-tabs">
            {apartmentTypes.map((ap, i) => {
              const active = i === typeIdx;
              return (
                <button
                  key={ap.key}
                  type="button"
                  role="tab"
                  aria-selected={active}
                  onClick={() => setTypeIdx(i)}
                  className={`group text-left rounded-xl px-3 md:px-4 py-2.5 md:py-3 border transition-all shrink-0 ${active ? 'border-[hsl(var(--primary))] bg-[hsl(var(--accent))] shadow-md' : 'border-border bg-[var(--surface-1)] hover:border-[hsl(var(--primary))]/40'}`}
                  data-testid={`apt-tab-${ap.key}`}
                >
                  <div className="text-[9px] md:text-[10px] uppercase tracking-widest text-[var(--gp-gold)]">{ap.key === 'rooftop' ? t('resortDetail.showcase.tabFlagship', 'Flagship') : t('resortDetail.showcase.tabSuite', 'Suite')}</div>
                  <div className="font-serif text-sm md:text-base lg:text-lg leading-tight mt-0.5 text-[hsl(var(--primary))]">{ap.name}</div>
                  <div className="flex items-center gap-1.5 md:gap-2 mt-1 text-[10px] md:text-[11px] text-muted-foreground whitespace-nowrap">
                    <span className="inline-flex items-center gap-1"><Maximize2 className="w-3 h-3" />{ap.sqm} mq</span>
                    <span>·</span>
                    <span className="font-medium text-foreground">{fmtEUR(ap.price)}</span>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Body: descrizione + carousel rooms */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-5">
          {/* Description column */}
          <div className="lg:col-span-1 order-2 lg:order-1">
            <div className="lg:sticky lg:top-6 p-4 md:p-5 rounded-2xl border border-border bg-[var(--surface-2)]">
              <div className="text-[10px] uppercase tracking-widest text-[var(--gp-gold)]">{type.tagline}</div>
              <h3 className="font-serif text-xl md:text-2xl text-[hsl(var(--primary))] mt-1">{type.name}</h3>
              <div className="mt-2 flex flex-wrap items-baseline gap-x-3 text-xs md:text-sm">
                <span className="font-medium text-[hsl(var(--primary))]">{type.sqm} mq</span>
                <span className="text-muted-foreground">·</span>
                <span className="font-semibold">{fmtEUR(type.price)}</span>
                <span className="text-muted-foreground">·</span>
                <span className="text-muted-foreground">{fmtEUR(type.pricePerSqm)}/mq</span>
              </div>
              <p className="text-xs md:text-sm text-muted-foreground mt-3 leading-relaxed">{type.description}</p>
              <ul className="mt-3 md:mt-4 space-y-1.5 md:space-y-2">
                {type.highlights.map((h) => (
                  <li key={h} className="flex items-start gap-2 text-xs md:text-sm">
                    <Check className="w-4 h-4 text-[hsl(var(--primary))] mt-0.5 shrink-0" />
                    <span>{h}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-4 md:mt-5 pt-3 md:pt-4 border-t border-border text-[11px] md:text-xs text-muted-foreground flex items-center gap-1.5">
                <Home className="w-3.5 h-3.5" /> {t('resortDetail.showcase.roomsHint', '{{count}} ambienti · scorrevoli', { count: type.rooms.length })}
              </div>
            </div>
          </div>

          {/* Rooms carousel */}
          <div className="lg:col-span-2 order-1 lg:order-2">
            <Carousel opts={CAROUSEL_OPTS} className="w-full" data-testid={`rooms-carousel-${type.key}`}>
              <CarouselContent className="-ml-3">
                {type.rooms.map((r, i) => (
                  <CarouselItem key={r.src} className="pl-3 basis-full sm:basis-4/5 md:basis-3/5">
                    <figure className="overflow-hidden rounded-2xl border border-border bg-[var(--surface-2)]">
                      <div className="aspect-[4/3] overflow-hidden bg-black/5">
                        <img
                          src={r.src}
                          alt={r.caption}
                          loading="lazy"
                          className="w-full h-full object-cover transition-transform duration-700 hover:scale-105"
                        />
                      </div>
                      <figcaption className="px-3 md:px-4 py-2.5 md:py-3 text-[11px] md:text-xs text-muted-foreground flex items-center justify-between gap-2 border-t border-border">
                        <span className="truncate">{r.caption}</span>
                        <span className="text-[10px] uppercase tracking-widest text-[var(--gp-gold)] shrink-0">{i + 1}/{type.rooms.length}</span>
                      </figcaption>
                    </figure>
                  </CarouselItem>
                ))}
              </CarouselContent>
              <CarouselPrevious className="hidden md:flex -left-2 bg-white border-border shadow-lg" data-testid={`rooms-prev-${type.key}`} />
              <CarouselNext className="hidden md:flex -right-2 bg-white border-border shadow-lg" data-testid={`rooms-next-${type.key}`} />
            </Carousel>
          </div>
        </div>
      </section>

      {/* Resort amenities showcase — hidden when showcase is empty (e.g. Lombok now uses gallery_groups instead) */}
      {showcase.length > 0 && (
        <section>
          <div className="mb-5 md:mb-6">
            <div className="text-[10px] md:text-xs tracking-[0.3em] uppercase text-muted-foreground">{t('resortDetail.showcase.resortEyebrow', 'Il resort')}</div>
            <h2 className="font-serif text-2xl md:text-3xl lg:text-5xl text-[hsl(var(--primary))] mt-1 leading-tight">{t('resortDetail.showcase.resortTitle', 'Masterplan, ristorante, gym, spa.')}</h2>
            <p className="text-xs md:text-sm text-muted-foreground mt-1.5 md:mt-2 max-w-2xl">
              {showcase.length === 1
                ? t('resortDetail.showcase.resortSubtitleOne', 'Uno sguardo immersivo su come si vive qui — dagli spazi comuni alla spa.')
                : t('resortDetail.showcase.resortSubtitleMany', '{{count}} sezioni immersive che raccontano come si vive qui — dagli spazi comuni alla spa.', { count: showcase.length })}
            </p>
          </div>

          <div className="space-y-6 md:space-y-8">
            {showcase.map((sec) => (
              <ShowcaseSection key={sec.key} section={sec} />
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

function ShowcaseSection({ section }) {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-5" data-testid={`showcase-${section.key}`}>
      <div className="lg:col-span-1 order-2 lg:order-1">
        <div className="p-4 md:p-5 rounded-2xl border border-border bg-[var(--surface-1)]">
          <div className="text-[10px] uppercase tracking-widest text-[var(--gp-gold)]">{section.subtitle}</div>
          <h3 className="font-serif text-xl md:text-2xl text-[hsl(var(--primary))] mt-1">{section.title}</h3>
          <ul className="mt-3 md:mt-4 space-y-1.5">
            {section.bullets.map((b) => (
              <li key={b} className="flex items-start gap-2 text-xs md:text-sm">
                <div className="w-1 h-1 rounded-full bg-[var(--gp-gold)] mt-2 shrink-0" />
                <span>{b}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
      <div className="lg:col-span-2 order-1 lg:order-2">
        <Carousel opts={CAROUSEL_OPTS} className="w-full">
          <CarouselContent className="-ml-3">
            {section.rooms.map((r, i) => (
              <CarouselItem key={r.src} className="pl-3 basis-full sm:basis-4/5 md:basis-3/5">
                <figure className="overflow-hidden rounded-2xl border border-border bg-[var(--surface-2)]">
                  <div className="aspect-[16/10] overflow-hidden bg-black/5">
                    <img src={r.src} alt={r.caption} loading="lazy" className="w-full h-full object-cover hover:scale-105 transition-transform duration-700" />
                  </div>
                  <figcaption className="px-3 md:px-4 py-2.5 md:py-3 text-[11px] md:text-xs text-muted-foreground flex items-center justify-between border-t border-border">
                    <span className="truncate">{r.caption}</span>
                    <span className="text-[10px] uppercase tracking-widest text-[var(--gp-gold)] shrink-0">{i + 1}/{section.rooms.length}</span>
                  </figcaption>
                </figure>
              </CarouselItem>
            ))}
          </CarouselContent>
          <CarouselPrevious className="hidden md:flex -left-2 bg-white border-border shadow-lg" />
          <CarouselNext className="hidden md:flex -right-2 bg-white border-border shadow-lg" />
        </Carousel>
      </div>
    </div>
  );
}
